import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ImagePlus } from 'lucide-react';
import ProductMediaPreview from './media/ProductMediaPreview';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import MediaDialog from './media/MediaDialog';

interface ProductMediaProps {
  mediaUrls: string[];
  previewMediaUrls: string[];
  addMediaUrl: (url: string) => void;
  removeMediaUrl: (index: number) => void;
  name: string;
  productId?: string;
  shopId?: string;
  removeExistingMediaUrl?: (index: number) => void;
}

const ProductMedia = ({ 
  mediaUrls, 
  previewMediaUrls, 
  addMediaUrl, 
  removeMediaUrl,
  removeExistingMediaUrl,
  name
}: ProductMediaProps) => {
  // Local state for media handling
  const [isMediaDialogOpen, setIsMediaDialogOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  // Filter out invalid URLs
  const validMediaUrls = Array.isArray(mediaUrls) 
    ? mediaUrls.filter(url => url && typeof url === 'string' && url.trim() !== '')
    : [];

  const validPreviewUrls = Array.isArray(previewMediaUrls)
    ? previewMediaUrls.filter(url => url && typeof url === 'string' && url.trim() !== '')
    : [];

  const handleAddImageClick = () => {
    setIsMediaDialogOpen(true);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);

    // Create data URL for the image
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setDataUrl(dataUrl);
      setPreviewUrl(dataUrl);
      setIsUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleConfirmImage = () => {
    if (dataUrl) {
      addMediaUrl(dataUrl);
      setPreviewUrl(null);
      setDataUrl(null);
      setIsMediaDialogOpen(false);
    }
  };

  const handleCancelImage = () => {
    setPreviewUrl(null);
    setDataUrl(null);
  };

  // Debug log to verify media URLs
  useEffect(() => {
    console.log("ProductMedia component rendering with URLs:", validMediaUrls);
    console.log("ProductMedia component rendering with preview URLs:", validPreviewUrls);
  }, [validMediaUrls, validPreviewUrls]);

  return (
    <Card className="bg-white rounded-md shadow">
      <CardHeader className="pb-3">
        <CardTitle>Images du produit</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Existing images */}
          {validMediaUrls.length > 0 && validMediaUrls.map((url, index) => (
            <ProductMediaPreview 
              key={`media-${index}-${url.substring(0, 10)}`}
              imageUrl={url} 
              name={name} 
              setImageUrl={() => removeExistingMediaUrl && removeExistingMediaUrl(index)}
              isPreview={false}
            />
          ))}
          
          {/* Preview images (newly added) */}
          {validPreviewUrls.length > 0 && validPreviewUrls.map((url, index) => (
            <ProductMediaPreview 
              key={`preview-${index}-${url.substring(0, 10)}`}
              imageUrl={url} 
              name={name} 
              setImageUrl={() => removeMediaUrl(index)}
              isPreview={true}
              isDataUrl={url.startsWith('data:')}
            />
          ))}
          
          {/* Button to add a new image */}
          <div className="border-2 border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center p-6 h-64">
            <Button 
              variant="outline" 
              onClick={handleAddImageClick}
              className="flex items-center gap-2"
            >
              <ImagePlus className="h-5 w-5" />
              Ajouter une image
            </Button>
          </div>
        </div>
      </CardContent>
      
      <Dialog open={isMediaDialogOpen} onOpenChange={setIsMediaDialogOpen}>
        <MediaDialog
          isOpen={isMediaDialogOpen}
          onOpenChange={setIsMediaDialogOpen}
          isUploading={isUploading}
          previewUrl={previewUrl}
          onFileChange={handleFileChange}
          onCancel={handleCancelImage}
          onConfirm={handleConfirmImage}
          hasImageToConfirm={!!previewUrl || !!dataUrl}
          requireConfirmation={true}
        />
      </Dialog>
    </Card>
  );
};

export default ProductMedia;
